import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, GraduationCap, HeartHandshake, Users } from "lucide-react";
import { KineticLine, FadeUp, Overline } from "../components/motion";
import { Marquee } from "../components/Marquee";
import { IMAGES } from "../constants/site";

const chapters = [
  {
    num: "01",
    icon: GraduationCap,
    title: "Every Student",
    text: "Every student — regardless of background, culture, abilities, or circumstances — deserves the opportunity, support, and encouragement to reach their full potential.",
  },
  {
    num: "02",
    icon: Users,
    title: "Support Educators",
    text: "Advocating for the educators who prepare young people to become confident, responsible, compassionate, and engaged members of society.",
  },
  {
    num: "03",
    icon: HeartHandshake,
    title: "Stronger Partnerships",
    text: "Listening to families and strengthening the partnership between schools and our communities, so every child feels valued, respected, and included.",
  },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-32 lg:pt-40">
      <div className="mx-auto grid max-w-7xl gap-12 px-6 pb-24 lg:grid-cols-12 lg:gap-0 lg:px-10">
        <div className="relative z-10 lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <Overline data-testid="hero-overline">School Board Trustee · Vaughan</Overline>
          </motion.div>

          <h1 className="mt-8 font-display text-5xl font-bold leading-[0.95] tracking-tighter text-ink sm:text-6xl md:text-7xl lg:text-[5.5rem]">
            <KineticLine delay={0.15}>A Community Voice</KineticLine>
            <KineticLine delay={0.28}>for Stronger</KineticLine>
            <KineticLine delay={0.41}>
              <span className="text-terracotta">Education.</span>
            </KineticLine>
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-xl text-base leading-relaxed text-stone2 md:text-lg"
          >
            For more than 30 years, I have proudly called York Region home — living, learning,
            working, and serving alongside the families who make this community so special. Now,
            I'm ready to bring that experience to our schools.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/get-involved"
              data-testid="hero-cta-volunteer"
              className="group inline-flex items-center gap-2 rounded-sm bg-terracotta px-7 py-3.5 font-semibold text-cream transition-all duration-300 hover:bg-terracotta-dark active:scale-[0.98]"
            >
              Join the Campaign
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/platform"
              data-testid="hero-cta-platform"
              className="inline-flex items-center gap-2 rounded-sm border border-ink/20 px-7 py-3.5 font-semibold text-ink transition-all duration-300 hover:border-terracotta hover:text-terracotta active:scale-[0.98]"
            >
              Read the Platform
            </Link>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative lg:-ml-16 lg:mt-10"
          >
            <div className="absolute -left-5 -top-5 h-full w-full border border-terracotta/40" aria-hidden="true" />
            <div className="relative overflow-hidden" style={{ clipPath: "polygon(0 0, 100% 0, 100% 92%, 88% 100%, 0 100%)" }}>
              <motion.img
                src={IMAGES.portrait}
                alt="Winnie Ho, School Board Trustee candidate"
                data-testid="hero-portrait"
                style={{ y }}
                className="h-[480px] w-full scale-110 object-cover object-top lg:h-[560px]"
              />
              <div
                className="absolute inset-0"
                style={{ background: "radial-gradient(ellipse at 50% 30%, transparent 55%, rgba(27,42,59,0.28) 100%)" }}
                aria-hidden="true"
              />
            </div>
            <div className="absolute -bottom-6 right-4 bg-navy px-6 py-4 text-cream shadow-none lg:-right-6">
              <p className="font-display text-2xl font-bold tracking-tight">30+ Years</p>
              <p className="text-xs uppercase tracking-[0.2em] text-stone-400">in York Region</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />

      <section data-testid="home-manifesto-preview" className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
        <FadeUp>
          <Overline>The Commitment</Overline>
          <h2 className="mt-6 max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight text-ink md:text-5xl lg:text-6xl">
            Education is fundamental to the{" "}
            <span className="font-serif italic text-terracotta">future</span> of Vaughan.
          </h2>
        </FadeUp>

        <div className="mt-20 grid gap-10 md:grid-cols-3 md:gap-8">
          {chapters.map((c, i) => (
            <FadeUp key={c.num} delay={i * 0.12} className={i === 1 ? "md:mt-12" : ""}>
              <Link
                to="/platform"
                data-testid={`home-chapter-${c.num}`}
                className="group relative block border border-sand bg-white p-8 transition-colors duration-300 hover:border-terracotta/50"
              >
                <span className="pointer-events-none absolute -top-7 right-4 -z-0 font-display text-8xl font-bold text-stone-200/70">
                  {c.num}
                </span>
                <c.icon size={28} className="text-terracotta" strokeWidth={1.5} />
                <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-ink">
                  {c.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-stone2">{c.text}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-terracotta">
                  Read the chapter
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      <section data-testid="home-quote-section" className="bg-navy py-28 lg:py-36">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <FadeUp>
            <blockquote className="font-serif text-3xl italic leading-snug text-cream md:text-4xl lg:text-[2.75rem]">
              "Together, we can work toward a school system where every child feels valued,
              respected, included, supported, and{" "}
              <span className="text-terracotta">empowered to succeed.</span>"
            </blockquote>
            <p className="mt-8 text-sm uppercase tracking-[0.25em] text-stone-400">
              — Winnie Ho
            </p>
          </FadeUp>
        </div>
      </section>

      <section data-testid="home-cta-section" className="mx-auto max-w-7xl px-6 py-28 lg:px-10">
        <div className="relative overflow-hidden border border-sand bg-white px-8 py-16 lg:px-16">
          <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-terracotta/10" aria-hidden="true" />
          <FadeUp>
            <h2 className="max-w-2xl font-display text-3xl font-bold leading-tight tracking-tight text-ink md:text-5xl">
              This campaign runs on neighbours like you.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-stone2 md:text-lg">
              Whether you can knock on doors, host a lawn sign, or simply spread the word — your
              voice matters in this movement for stronger schools.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/get-involved"
                data-testid="home-cta-volunteer"
                className="group inline-flex items-center gap-2 rounded-sm bg-terracotta px-7 py-3.5 font-semibold text-cream transition-all duration-300 hover:bg-terracotta-dark active:scale-[0.98]"
              >
                Become a Volunteer
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="home-cta-contact"
                className="inline-flex items-center gap-2 rounded-sm border border-ink/20 px-7 py-3.5 font-semibold text-ink transition-all duration-300 hover:border-terracotta hover:text-terracotta active:scale-[0.98]"
              >
                Send a Message
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
